import { authAPI } from "./authApi";
import { resolveApiBaseUrl } from "./apiBase";

const API_BASE_URL = resolveApiBaseUrl();

export interface InboxItem {
  uid: string;
  text: string;
  createdAt: string;
  _updated?: string;
}

export interface AssignInboxItemParams {
  collectionSlug: string;
  fieldName: string;
  organization?: string;
}

export interface AssignInboxItemResult {
  success: boolean;
  item: Record<string, unknown>;
  collectionSlug: string;
}

async function inboxRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = authAPI.getIdToken();
  const url = `${API_BASE_URL}${path}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let parsed: any = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { message: text };
    }
  }

  if (!response.ok) {
    const message =
      (parsed && typeof parsed.message === "string" && parsed.message) ||
      text ||
      `Request failed with status ${response.status}`;
    const err = new Error(message) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parsed as T;
}

export async function listInboxItems(): Promise<InboxItem[]> {
  const data = await inboxRequest<{ success: boolean; items: InboxItem[] }>("/inbox");
  return Array.isArray(data?.items) ? data.items : [];
}

export async function createInboxItem(text: string): Promise<InboxItem> {
  const data = await inboxRequest<{ success: boolean; item: InboxItem }>("/inbox", {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  return data.item;
}

export async function assignInboxItem(
  uid: string,
  params: AssignInboxItemParams
): Promise<AssignInboxItemResult> {
  return inboxRequest<AssignInboxItemResult>(`/inbox/${encodeURIComponent(uid)}/assign`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function deleteInboxItem(uid: string): Promise<void> {
  await inboxRequest<void>(`/inbox/${encodeURIComponent(uid)}`, {
    method: "DELETE",
  });
}
